import {
  AdminFormGrid,
  Button,
  Card,
  CardHeader,
  FormField,
  PhoneInput,
  Select,
  TextInput,
} from "@constructorfleet/ui/design-system";
import { useToast } from "@constructorfleet/ui";
import { useMemo } from "react";
import type { BhgCallSettings, ProductDbClient } from "@constructorfleet/bhg-product-core";
import { saveBhgCallSettings } from "@constructorfleet/bhg-product-core";

function validateRuntimeSettings(settings: BhgCallSettings) {
  const errors: Record<string, string> = {};
  if (settings.maxActiveCallsPerDialFromNumber < 1) {
    errors.maxActiveCalls = "Must be at least 1.";
  }
  if (settings.maxCallDurationSeconds < 1) {
    errors.maxDuration = "Must be at least 1 second.";
  }
  if (settings.webhookStatusCallbackBaseUrl.trim().length > 0) {
    try {
      new URL(settings.webhookStatusCallbackBaseUrl);
    } catch {
      errors.webhookUrl = "Must be a valid URL.";
    }
  }
  if (settings.websiteBaseUrl.trim().length > 0) {
    try {
      new URL(settings.websiteBaseUrl);
    } catch {
      errors.websiteUrl = "Must be a valid URL.";
    }
  }
  if (settings.failureWebhookUrl.trim().length > 0) {
    try {
      new URL(settings.failureWebhookUrl);
    } catch {
      errors.failureWebhookUrl = "Must be a valid URL.";
    }
    if (settings.failureWebhookSecret.trim().length === 0) {
      errors.failureWebhookSecret = "Required so receivers can verify the signature.";
    }
  }
  return errors;
}

export function RuntimeSettingsForm({
  settings,
  setSettings,
  isRuntimeDirty,
  onSaved,
  dbClient,
}: {
  settings: BhgCallSettings | null;
  setSettings: React.Dispatch<React.SetStateAction<BhgCallSettings | null>>;
  isRuntimeDirty: boolean;
  onSaved: () => void;
  dbClient: ProductDbClient;
}) {
  const { pushToast } = useToast();

  if (!settings) return null;

  const validationErrors = useMemo(() => validateRuntimeSettings(settings), [settings]);
  const hasErrors = Object.keys(validationErrors).length > 0;
  const runtimeStatus = hasErrors
    ? "Fix validation issues before saving."
    : isRuntimeDirty
      ? "Draft changes are ready to review."
      : "Runtime settings are in sync.";

  return (
    <Card wide>
      <CardHeader title="Runtime Settings" meta={runtimeStatus} />
      <form
        className="card-body ds-stack"
        onSubmit={(event) => {
          event.preventDefault();
          if (hasErrors) return;
          void (async () => {
            const result = await saveBhgCallSettings(settings, dbClient);
            if (result.ok) {
              onSaved();
              pushToast("Runtime settings saved.", "success");
            } else {
              pushToast(result.error, "danger");
            }
          })();
        }}
      >
        <div className="admin-form">
          <h4>Call Routing</h4>
          <p className="hc-desc">
            Set the numbers and callback endpoint used when the platform places outbound calls.
          </p>
          <FormField
            label="Dial from numbers"
            htmlFor="admin-dial-from-phone-0"
            helpText="US numbers formatted as full +1 numbers."
          >
            <div className="ds-stack">
              {settings.dialFromPhoneNumbers.map((value, index) => (
                <div key={`dial-from-${index}`} className="shell-actions">
                  <PhoneInput
                    id={`admin-dial-from-phone-${index}`}
                    value={value}
                    valueFormat="e164"
                    onChange={(nextValue) => {
                      setSettings((current) =>
                        current
                          ? {
                              ...current,
                              dialFromPhoneNumbers: current.dialFromPhoneNumbers.map((item, i) =>
                                i === index ? nextValue : item
                              ),
                            }
                          : current
                      );
                    }}
                    required
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={settings.dialFromPhoneNumbers.length <= 1}
                    onClick={() => {
                      setSettings((current) =>
                        current
                          ? {
                              ...current,
                              dialFromPhoneNumbers: current.dialFromPhoneNumbers.filter(
                                (_, i) => i !== index
                              ),
                            }
                          : current
                      );
                    }}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <div className="shell-actions">
                <Button
                  type="button"
                  size="sm"
                  variant="brand"
                  onClick={() => {
                    setSettings((current) =>
                      current
                        ? {
                            ...current,
                            dialFromPhoneNumbers: [...current.dialFromPhoneNumbers, ""],
                          }
                        : current
                    );
                  }}
                >
                  Add dial-from number
                </Button>
              </div>
            </div>
          </FormField>
          <AdminFormGrid>
            <FormField label="Dial to number" htmlFor="admin-dial-to-phone">
              <PhoneInput
                id="admin-dial-to-phone"
                value={settings.dialToPhoneNumber}
                valueFormat="e164"
                onChange={(nextValue) => {
                  setSettings((current) =>
                    current ? { ...current, dialToPhoneNumber: nextValue } : current
                  );
                }}
                required
              />
            </FormField>
            <FormField
              label="Webhook callback URL"
              htmlFor="admin-webhook-status-callback-base-url"
              errorText={validationErrors.webhookUrl}
            >
              <TextInput
                id="admin-webhook-status-callback-base-url"
                type="url"
                value={settings.webhookStatusCallbackBaseUrl}
                onChange={(event) => {
                  const nextValue = (event.target as HTMLInputElement)?.value ?? "";
                  setSettings((current) =>
                    current ? { ...current, webhookStatusCallbackBaseUrl: nextValue } : current
                  );
                }}
                required
              />
            </FormField>
            <FormField
              label="Patient website URL"
              htmlFor="admin-website-base-url"
              helpText="Origin used for call links in patient notifications. Leave blank to omit links."
              errorText={validationErrors.websiteUrl}
            >
              <TextInput
                id="admin-website-base-url"
                type="url"
                value={settings.websiteBaseUrl}
                onChange={(event) => {
                  const nextValue = (event.target as HTMLInputElement)?.value ?? "";
                  setSettings((current) =>
                    current ? { ...current, websiteBaseUrl: nextValue } : current
                  );
                }}
              />
            </FormField>
          </AdminFormGrid>
        </div>

        <div className="admin-form">
          <h4>Failure Alerting</h4>
          <p className="hc-desc">
            Receives every call, SMS, email and skipped-notification failure, separately from
            per-user webhooks. Leave the URL blank to disable.
          </p>
          <AdminFormGrid>
            <FormField
              label="Failure webhook URL"
              htmlFor="admin-failure-webhook-url"
              errorText={validationErrors.failureWebhookUrl}
            >
              <TextInput
                id="admin-failure-webhook-url"
                type="url"
                value={settings.failureWebhookUrl}
                onChange={(event) => {
                  const nextValue = (event.target as HTMLInputElement)?.value ?? "";
                  setSettings((current) =>
                    current ? { ...current, failureWebhookUrl: nextValue } : current
                  );
                }}
              />
            </FormField>
            <FormField
              label="Signing secret"
              htmlFor="admin-failure-webhook-secret"
              helpText="Payloads are signed HMAC-SHA256 in the X-CF-Signature header."
              errorText={validationErrors.failureWebhookSecret}
            >
              <TextInput
                id="admin-failure-webhook-secret"
                type="password"
                value={settings.failureWebhookSecret}
                onChange={(event) => {
                  const nextValue = (event.target as HTMLInputElement)?.value ?? "";
                  setSettings((current) =>
                    current ? { ...current, failureWebhookSecret: nextValue } : current
                  );
                }}
              />
            </FormField>
          </AdminFormGrid>
        </div>

        <div className="admin-form">
          <h4>Safety Limits</h4>
          <p className="hc-desc">
            Cap concurrency and call duration so one bad run cannot monopolize the outbound pool.
          </p>
          <AdminFormGrid>
            <FormField
              label="Max active calls per phone number"
              htmlFor="admin-max-active-calls-per-dial-from-number"
              errorText={validationErrors.maxActiveCalls}
            >
              <TextInput
                id="admin-max-active-calls-per-dial-from-number"
                type="number"
                min={1}
                value={String(settings.maxActiveCallsPerDialFromNumber)}
                onChange={(event) => {
                  const raw = (event.target as HTMLInputElement)?.value ?? "";
                  const nextValue = Number(raw || 1);
                  setSettings((current) =>
                    current
                      ? {
                          ...current,
                          maxActiveCallsPerDialFromNumber: nextValue,
                        }
                      : current
                  );
                }}
                required
              />
            </FormField>
            <FormField
              label="Max duration (sec)"
              htmlFor="admin-max-call-duration-seconds"
              errorText={validationErrors.maxDuration}
            >
              <TextInput
                id="admin-max-call-duration-seconds"
                type="number"
                min={1}
                value={String(settings.maxCallDurationSeconds)}
                onChange={(event) => {
                  const raw = (event.target as HTMLInputElement)?.value ?? "";
                  const nextValue = Number(raw || 1);
                  setSettings((current) =>
                    current ? { ...current, maxCallDurationSeconds: nextValue } : current
                  );
                }}
                required
              />
            </FormField>
          </AdminFormGrid>
        </div>

        <div className="admin-form">
          <h4>Provider Credentials</h4>
          <p className="hc-desc">
            Keep only the active provider credentials current so handoffs between test and
            production remain obvious.
          </p>
          <AdminFormGrid>
            <FormField label="Voice provider" htmlFor="admin-voice-provider">
              <Select
                id="admin-voice-provider"
                value={settings.voiceProvider}
                onChange={(event) => {
                  const nextValue =
                    ((event.target as HTMLSelectElement)?.value as "twilio" | "telnyx") ?? "twilio";
                  setSettings((current) =>
                    current ? { ...current, voiceProvider: nextValue } : current
                  );
                }}
              >
                <option value="twilio">Twilio</option>
                <option value="telnyx">Telnyx</option>
              </Select>
            </FormField>
            {settings.voiceProvider === "twilio" ? (
              <>
                <FormField label="Twilio Account SID" htmlFor="admin-twilio-account-sid">
                  <TextInput
                    id="admin-twilio-account-sid"
                    value={settings.twilioAccountSid}
                    onChange={(event) => {
                      const value = (event.target as HTMLInputElement)?.value ?? "";
                      setSettings((current) =>
                        current ? { ...current, twilioAccountSid: value } : current
                      );
                    }}
                  />
                </FormField>
                <FormField label="Twilio Auth Token" htmlFor="admin-twilio-auth-token">
                  <TextInput
                    id="admin-twilio-auth-token"
                    type="password"
                    value={settings.twilioAuthToken}
                    onChange={(event) => {
                      const value = (event.target as HTMLInputElement)?.value ?? "";
                      setSettings((current) =>
                        current ? { ...current, twilioAuthToken: value } : current
                      );
                    }}
                  />
                </FormField>
              </>
            ) : (
              <>
                <FormField label="Telnyx API Key" htmlFor="admin-telnyx-api-key">
                  <TextInput
                    id="admin-telnyx-api-key"
                    type="password"
                    value={settings.telnyxApiKey}
                    onChange={(event) => {
                      const value = (event.target as HTMLInputElement)?.value ?? "";
                      setSettings((current) =>
                        current ? { ...current, telnyxApiKey: value } : current
                      );
                    }}
                  />
                </FormField>
                <FormField label="Telnyx Public Key" htmlFor="admin-telnyx-public-key">
                  <TextInput
                    id="admin-telnyx-public-key"
                    value={settings.telnyxPublicKey}
                    onChange={(event) => {
                      const value = (event.target as HTMLInputElement)?.value ?? "";
                      setSettings((current) =>
                        current ? { ...current, telnyxPublicKey: value } : current
                      );
                    }}
                  />
                </FormField>
                <FormField label="Telnyx Connection ID" htmlFor="admin-telnyx-connection-id">
                  <TextInput
                    id="admin-telnyx-connection-id"
                    value={settings.telnyxConnectionId}
                    onChange={(event) => {
                      const value = (event.target as HTMLInputElement)?.value ?? "";
                      setSettings((current) =>
                        current ? { ...current, telnyxConnectionId: value } : current
                      );
                    }}
                  />
                </FormField>
              </>
            )}
          </AdminFormGrid>
        </div>

        <div className="shell-actions">
          <Button type="submit" disabled={!isRuntimeDirty || hasErrors}>
            Save runtime settings
          </Button>
        </div>
      </form>
    </Card>
  );
}
